import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Alert, Badge, Button, Card, CloseButton, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  connectGoogleOAuthAPI,
  getGoogleSetupAPI,
  runCalendarTestAPI,
  syncGoogleUsersAPI,
  testMeetArtifactAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { companyUrl } from '../../../utils/mhNav';
import styles from './GoogleSetup.module.scss';

const STEPS = ['Auth', 'Calendar Test', 'User Sync', 'Meet Artifact'];

export default function GoogleSetup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [dismissToken, setDismissToken] = useState(false);
  const [syncScope, setSyncScope] = useState({ value: 'all_org', label: 'All organization' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getGoogleSetupAPI();
      setData(res.data);
      setSyncScope(
        res.data.userSync?.scope === 'specific_ou'
          ? { value: 'specific_ou', label: 'Specific OU' }
          : { value: 'all_org', label: 'All organization' }
      );
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const exp = data?.tokenExpiresAt ? new Date(data.tokenExpiresAt).getTime() : 0;
  const daysLeft = Math.ceil((exp - Date.now()) / 86400000);
  const showTokenWarn = data?.connected && daysLeft < 7 && daysLeft >= 0 && !dismissToken;

  if (loading && !data) {
    return (
      <div>
        <PageHeader title="Google" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Integrations', path: companyUrl('integrations') }, { label: 'Google' }]} />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <PageHeader title="Google" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Integrations', path: companyUrl('integrations') }]} />
        <EmptyState icon={AlertTriangle} title="Error" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Google Workspace setup"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Integrations', path: companyUrl('integrations') },
          { label: 'Google' },
        ]}
      />

      {showTokenWarn && (
        <Alert variant="warning" className="d-flex justify-content-between align-items-start">
          <span>Access token expires in {daysLeft} day(s).</span>
          <CloseButton onClick={() => setDismissToken(true)} />
        </Alert>
      )}

      <div className={styles.stepper}>
        {STEPS.map((l, i) => (
          <button key={l} type="button" className={`${styles.step} ${step === i + 1 ? styles.stepOn : ''}`} onClick={() => setStep(i + 1)}>
            {i + 1}. {l}
          </button>
        ))}
      </div>

      {step === 1 && (
        <Card>
          <Card.Body>
            <Button
              className="mb-3"
              onClick={async () => {
                try {
                  await connectGoogleOAuthAPI();
                  toast.success('OAuth complete (mock)');
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Connect Google
            </Button>
            {data.connected && (
              <>
                <p>
                  Connected: <strong>{data.accountEmail}</strong>
                </p>
                <p className="small">
                  Token valid until {new Date(data.tokenExpiresAt).toLocaleString()}{' '}
                  <Badge bg="info">{daysLeft}d left</Badge>
                </p>
              </>
            )}
            <h3 className="h6 mt-3">Scopes</h3>
            <ul className="small">
              {(data.scopes || []).map((s) => (
                <li key={s.name}>
                  {s.granted ? '✓' : '✗'} {s.name}
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <Card.Body>
            <Button
              className="mb-3"
              onClick={async () => {
                try {
                  await runCalendarTestAPI();
                  toast.success('Calendar test OK');
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Run calendar booking test
            </Button>
            <p className="small text-muted">
              Last: {data.calendarTest?.lastAt ? new Date(data.calendarTest.lastAt).toLocaleString() : '—'} —{' '}
              {data.calendarTest?.success ? 'Success' : 'Not run'}
            </p>
          </Card.Body>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <Card.Body>
            <Button
              className="mb-3"
              onClick={async () => {
                try {
                  await syncGoogleUsersAPI(syncScope.value);
                  toast.success('Sync queued');
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Sync users now
            </Button>
            <p className="small">
              Last sync: {data.userSync?.lastAt ? new Date(data.userSync.lastAt).toLocaleString() : '—'} — {data.userSync?.count} users
            </p>
            <Form.Label>Scope</Form.Label>
            <Form.Select
              value={syncScope.value}
              onChange={(e) =>
                setSyncScope(
                  e.target.value === 'specific_ou'
                    ? { value: 'specific_ou', label: 'Specific OU' }
                    : { value: 'all_org', label: 'All organization' }
                )
              }
            >
              <option value="all_org">All organization</option>
              <option value="specific_ou">Specific OU</option>
            </Form.Select>
          </Card.Body>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <Card.Body>
            <p>Meet artifact: {data.meetArtifact?.ok ? 'OK' : 'Not verified'}</p>
            <Button
              onClick={async () => {
                try {
                  await testMeetArtifactAPI();
                  toast.success('Artifact pull OK');
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Test artifact pull
            </Button>
            {data.meetArtifact?.lastSample && <Alert className="mt-3 small mb-0">{data.meetArtifact.lastSample}</Alert>}
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
